import type { DashboardRole } from "./types";

export const DASHBOARD_SLOW_REQUEST_MS = 1_000;

export type DashboardMonitoringEndpoint =
  | "analytics"
  | "operations"
  | "ranking"
  | "detail";

export type DashboardRequestMonitoringEvent = {
  kind: "request";
  endpoint: DashboardMonitoringEndpoint;
  role: DashboardRole;
  outcome: "success" | "error" | "cancelled";
  durationMs: number;
  slow: boolean;
  errorName?: string;
};

export type DashboardRenderFailureEvent = {
  kind: "render_failure";
  role: DashboardRole;
  surface: "home" | "detail";
  errorName: string;
};

export type DashboardMonitoringEvent =
  | DashboardRequestMonitoringEvent
  | DashboardRenderFailureEvent;

type DashboardMonitoringObserver = (event: DashboardMonitoringEvent) => void;
type DashboardClock = () => number;

const observers = new Set<DashboardMonitoringObserver>();

const defaultClock: DashboardClock = () =>
  globalThis.performance?.now?.() ?? Date.now();

const SAFE_ERROR_NAMES = new Set([
  "AggregateError",
  "ApiError",
  "Error",
  "EvalError",
  "RangeError",
  "ReferenceError",
  "SyntaxError",
  "TypeError",
  "URIError",
]);

const errorName = (error: unknown): string =>
  error instanceof Error && SAFE_ERROR_NAMES.has(error.name)
    ? error.name
    : "UnknownError";

const isAbortError = (error: unknown): boolean =>
  typeof error === "object" &&
  error !== null &&
  "name" in error &&
  error.name === "AbortError";

const publish = (event: DashboardMonitoringEvent): void => {
  const safeEvent = Object.freeze({ ...event }) as DashboardMonitoringEvent;
  for (const observer of observers) {
    try {
      observer(safeEvent);
    } catch {
      // Monitoring must never affect dashboard behavior.
    }
  }
};

export function observeDashboardMonitoring(
  observer: DashboardMonitoringObserver,
): () => void {
  observers.add(observer);
  return () => observers.delete(observer);
}

export async function monitorDashboardRequest<Value>(
  input: {
    endpoint: DashboardMonitoringEndpoint;
    role: DashboardRole;
  },
  request: () => Promise<Value>,
  clock: DashboardClock = defaultClock,
): Promise<Value> {
  const startedAt = clock();

  try {
    const value = await request();
    const durationMs = Math.max(0, clock() - startedAt);
    publish({
      kind: "request",
      ...input,
      outcome: "success",
      durationMs,
      slow: durationMs >= DASHBOARD_SLOW_REQUEST_MS,
    });
    return value;
  } catch (error) {
    const durationMs = Math.max(0, clock() - startedAt);
    const cancelled = isAbortError(error);
    publish({
      kind: "request",
      ...input,
      outcome: cancelled ? "cancelled" : "error",
      durationMs,
      slow: durationMs >= DASHBOARD_SLOW_REQUEST_MS,
      ...(cancelled ? {} : { errorName: errorName(error) }),
    });
    throw error;
  }
}

export function reportDashboardRenderFailure(input: {
  role: DashboardRole;
  surface: DashboardRenderFailureEvent["surface"];
  error: unknown;
}): void {
  publish({
    kind: "render_failure",
    role: input.role,
    surface: input.surface,
    errorName: errorName(input.error),
  });
}
